import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import OrganisationUnit from 'features/organisation/domain/models/organisation-unit';
import ExplanationLink from '../domain/models/explanationLink';
import ExplanationLinkCommand from '../domain/models/explanationLink-command';
import ReadExplanationLinksResponse from '../domain/models/read-explanationLinks-response';

const settingsBaseUrl = '/api/v1/Settings';
const organizationBaseUrl = '/api/v1/OrganisationUnit';

enum ExplanationLinksQueryPaths {
  CreateExplanationLink = 'CreateExplanationLink',
  ReadExplanationLinks = 'ReadExplanationLinks',
  UpdateExplanationLink = 'UpdateExplanationLink',
  DeleteExplanationLink = 'DeleteExplanationLink',
}

enum OrganizationQueryPaths {
  ReadOrganisationUnitsForSettings = 'ReadOrganisationUnitsForSettings',
}

const createExplanationLink = async (axiosInstance: AxiosInstance, body: ExplanationLinkCommand): Promise<ExplanationLink> => {
  const response = await axiosInstance.post(`${settingsBaseUrl}/${ExplanationLinksQueryPaths.CreateExplanationLink}`, body);
  return response.data;
};

const readExplanationLinks = async (axiosInstance: AxiosInstance): Promise<ReadExplanationLinksResponse> => {
  const response = await axiosInstance.get(`${settingsBaseUrl}/${ExplanationLinksQueryPaths.ReadExplanationLinks}`);
  return response.data;
};

const updateExplanationLink = async (axiosInstance: AxiosInstance, body: ExplanationLinkCommand): Promise<ExplanationLink> => {
  const response = await axiosInstance.put(`${settingsBaseUrl}/${ExplanationLinksQueryPaths.UpdateExplanationLink}`, body);
  return response.data;
};

const deleteExplanationLink = async (axiosInstance: AxiosInstance, id: string): Promise<void> => {
  await axiosInstance.delete(`${settingsBaseUrl}/${ExplanationLinksQueryPaths.DeleteExplanationLink}/${id}`);
};

const readOrganisationUnitsForSettings = async (axiosInstance: AxiosInstance): Promise<OrganisationUnit[]> => {
  const response = await axiosInstance.get(`${organizationBaseUrl}/${OrganizationQueryPaths.ReadOrganisationUnitsForSettings}`);
  return response.data;
};

export const useReadExplanationLinks = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [ExplanationLinksQueryPaths.ReadExplanationLinks],
    queryFn: () => readExplanationLinks(authenticatedInstance),
  });
};

export const useCreateExplanationLink = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ExplanationLinkCommand) => createExplanationLink(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ExplanationLinksQueryPaths.ReadExplanationLinks] });
    },
  });
};

export const useUpdateExplanationLink = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ExplanationLinkCommand) => updateExplanationLink(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ExplanationLinksQueryPaths.ReadExplanationLinks] });
    },
  });
};

export const useDeleteExplanationLink = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteExplanationLink(authenticatedInstance, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ExplanationLinksQueryPaths.ReadExplanationLinks] });
    },
  });
};

export const useReadOrganisationUnitsForSettings = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganizationQueryPaths.ReadOrganisationUnitsForSettings],
    queryFn: () => readOrganisationUnitsForSettings(authenticatedInstance),
  });
};
